import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Phone, Send, Facebook, Instagram, 
  MessageCircle, Linkedin, CheckCircle, X 
} from 'lucide-react';
import { Helmet } from "react-helmet";

function Contact() {
  const [status, setStatus] = useState(null);
  const [smsConsent, setSmsConsent] = useState(false);
  
  const contactBg = "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069";

  const socialIcons = [
    { icon: <Facebook size={18} />, link: "https://www.facebook.com/share/1CWqGBmxyK/", color: "hover:bg-blue-600" },
    { icon: <Instagram size={18} />, link: "https://www.instagram.com/zygmasolution?igsh=MTBneHBhbG16Mzljdg==", color: "hover:bg-pink-600" },
   { 
  icon: <MessageCircle size={18} />, 
  link: "https://wa.me/18035409934?text=Hello%20Zygma%20Growth%20Solutions,%20I%20need%20your%20help!", 
  color: "hover:bg-[#25D366] hover:text-white transition-colors duration-300",
  label: "WhatsApp" 
},
    { icon: <Linkedin size={18} />, link: "https://www.linkedin.com/in/zygma-growth-solutions-a3105a295/", color: "hover:bg-blue-700" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);

    try {
      const response = await fetch("https://formspree.io/f/mnjbapag", {
        method: "POST",
        body: data,
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        setStatus("success");
        setSmsConsent(false);
        form.reset();
        setTimeout(() => setStatus(null), 5000);
      } else {
        setStatus("error");
      }
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-12 md:py-24 bg-white relative">
      <Helmet>
        <title>Contact ZYGMA | Global Support & Business Inquiries</title>
        <meta name="description" content="Get in touch with ZYGMA Growth Solutions. We provide worldwide digital marketing support, GMB audits, and web development consultations." />
        <link rel="canonical" href="https://zygmagrowthsolutions.com/contact" />
      </Helmet>

      <AnimatePresence>
        {status === "success" && (
          <motion.div 
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 20 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-5 left-1/2 -translate-x-1/2 z-[100] bg-green-600 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 min-w-[300px]"
          >
            <CheckCircle size={24} />
            <span className="font-bold">Message sent successfully!</span>
            <button onClick={() => setStatus(null)} className="ml-auto"><X size={18} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="relative rounded-t-[2rem] md:rounded-t-[3.5rem] overflow-hidden min-h-[200px] md:min-h-[300px] flex items-center justify-center text-center shadow-xl">
          <div className="absolute inset-0 z-0">
            <img src={contactBg} alt="Contact ZYGMA Worldwide" className="w-full h-full object-cover opacity-70" />
            <div className="absolute inset-0 bg-slate-900/40"></div>
          </div>
          <div className="relative z-10 px-4">
            <h2 className="text-3xl md:text-5xl font-black text-white drop-shadow-2xl">
              Global Reach. <span className="text-blue-400 block md:inline">Personal Connection.</span>
            </h2>
          </div>
        </div>

        <div className="bg-slate-50 rounded-b-[2rem] md:rounded-b-[3.5rem] border-x border-b border-slate-100 shadow-2xl p-6 md:p-16">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-16">
            
            <div className="space-y-8 md:space-y-10">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Worldwide Support</h3>
                <p className="text-slate-600 text-base md:text-lg">Our team operates globally to provide you with the best digital growth solutions within 24 hours.</p>
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-4 md:gap-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shrink-0"><Phone size={20} /></div>
                  <div><p className="text-xs text-slate-400 font-bold uppercase">WhatsApp Global</p><p className="text-lg md:text-xl font-black text-slate-800">+18035409934</p></div>
                </div>
                <div className="flex items-center gap-4 md:gap-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shrink-0"><Mail size={20} /></div>
                  <div className="overflow-hidden">
                    <p className="text-xs text-slate-400 font-bold uppercase">Official Email</p>
                    <p className="text-base md:text-xl font-black text-slate-800 break-words">info@zygmagrowthsolutions.com</p>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-slate-200 text-center md:text-left">
                <p className="text-sm text-slate-500 font-bold uppercase mb-4 tracking-widest">Connect With Us</p>
                <div className="flex flex-wrap justify-center md:justify-start gap-3">
                  {socialIcons.map((social, i) => (
                    <a key={i} href={social.link} target="_blank" rel="noopener noreferrer" className={`w-12 h-12 flex items-center justify-center rounded-xl bg-white border border-slate-200 transition-all duration-300 ${social.color} hover:text-white hover:-translate-y-2 shadow-sm`}>
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <motion.div className="bg-white p-6 md:p-10 rounded-[2rem] md:rounded-[2.5rem] shadow-xl border border-slate-100">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input name="First Name" type="text" placeholder="First Name" required className="input-style" />
                  <input name="Last Name" type="text" placeholder="Last Name" required className="input-style" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input name="Email" type="email" placeholder="Email Address" required className="input-style" />
                  <input name="Phone" type="tel" placeholder="Phone Number" required className="input-style" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input name="Country" type="text" placeholder="Your Country" required className="input-style" />
                  <select name="Service Needed" required className="input-style">
                    <option value="">Select a Service</option>
                    <option value="GMB Optimization">GMB Optimization</option>
                    <option value="Web Development">Web Development</option>
                    <option value="SEO Strategy">Full SEO Strategy</option>
                    <option value="Global Branding">Global Branding</option>
                  </select>
                </div>
                <textarea name="Description" placeholder="Project details or questions..." rows="4" required className="input-style w-full"></textarea>
                
                {/* --- COMPLIANCE UPDATED: "Alerts" Removed as per Steve's request --- */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mt-2">
                  <div className="flex items-start gap-3">
                    <input 
                      id="smsConsent"
                      name="smsConsent" 
                      type="checkbox" 
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <label htmlFor="smsConsent" className="text-[11px] md:text-[12px] text-slate-600 leading-relaxed cursor-pointer select-none">
                      I agree to receive promotional marketing text messages from <strong>Zygma Growth Solutions</strong>. 
                      Message frequency varies. Message and data rates may apply. 
                      Reply <strong>STOP</strong> to unsubscribe or <strong>HELP</strong> for help. 
                      View our <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>.
                    </label>
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  disabled={!smsConsent} 
                  className={`w-full font-bold py-4 md:py-5 rounded-xl flex items-center justify-center gap-3 transition-all group shadow-lg ${smsConsent ? 'bg-slate-900 hover:bg-blue-600 text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
                >
                  {status === "success" ? "Message Received!" : "Send Worldwide Inquiry"} 
                  <Send size={20} className={smsConsent ? "group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" : ""} />
                </button>

                {status === "error" && <p className="text-red-500 text-sm font-bold text-center">Connection error. Please try again.</p>}
              </form>
            </motion.div>

          </div>
        </div>
      </div>

      <style jsx>{`
        .input-style {
          width: 100%;
          background-color: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 0.75rem;
          padding: 0.875rem 1.25rem;
          outline: none;
          transition: all 0.3s;
          font-size: 16px;
        }
        .input-style:focus {
          border-color: #2563eb;
          background-color: #fff;
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
        }
      `}</style>
    </section>
  );
}

export default Contact;